import {
  Company,
  PaginatedCompany,
  CompanyContact,
  CompanyQueriesFilter,
} from 'types/company'
import { getApiUrl } from 'modules/enrollment-platform-api'
import axios from 'axios'
import { DEFAULT_PAGINATION_LIMIT } from 'enrollment-platform/pages/CustomerManagement/pages/ListCustomer/constants'

/**
 * Fetches companies from the API
 * @returns Promise<Company[]> - The raw response containing full company data
 */
export const fetchCompanies = async (
  filters?: CompanyQueriesFilter
): Promise<PaginatedCompany> => {
  const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
  const response = await axios.get(`/customers`, {
    baseURL: getApiUrl(),
    params: {
      ...filters,
      page: filters?.page || 1,
      limit: filters?.limit || DEFAULT_PAGINATION_LIMIT,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })

  return response.data
}

/**
 * Fetches every page from `/customers` and concatenates results.
 * Use this for dropdowns that must list all companies; a single `fetchCompanies` call
 * defaults to {@link DEFAULT_PAGINATION_LIMIT} and omits later pages.
 */
export const fetchAllCompanies = async (
  filters?: Omit<CompanyQueriesFilter, 'page' | 'limit'>
): Promise<PaginatedCompany> => {
  const limit = DEFAULT_PAGINATION_LIMIT
  let page = 1
  const accumulated: Company[] = []

  // Stop when a page is empty or shorter than `limit` (last page). Do not rely on
  // `totalPages` alone — a wrong meta would truncate the list for dropdowns.
  const maxPage = 500

  for (;;) {
    const result = await fetchCompanies({ ...filters, page, limit })
    accumulated.push(...result.data)

    if (result.data.length === 0 || result.data.length < limit) break
    if (page >= maxPage) break

    page += 1
  }

  return {
    data: accumulated,
    pagination: {
      page: 1,
      limit: accumulated.length,
      total: accumulated.length,
      totalPages: 1,
    },
  }
}

/**
 * Fetches companies the current user is allowed to administer.
 * Uses /api/v1/members/me/administered-customers which handles role-based
 * filtering server-side (PlatformHixmeAdmin sees all, ClientAdmin/Broker
 * see only their administered clients).
 */
export const fetchAdministeredCompanies = async (): Promise<Company[]> => {
  const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
  const response = await axios.get<Company[]>(
    `/api/v1/members/me/administered-customers`,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )

  return response.data ?? []
}

/**
 * Fetches a single company by ID from the enrollment platform API
 *
 * @param companyId - The ID of the company to fetch
 * @returns Promise<Company> - The company data
 */
export const fetchCompanyById = async (companyId: string): Promise<Company> => {
  const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
  const response = await axios.get(`/customers/${companyId}`, {
    baseURL: getApiUrl(),
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
  })
  return response.data
}

/**
 * Updates a company contact using the enrollment platform API
 *
 * @param companyId - The ID of the company
 * @param contactData - The contact data to update
 * @returns Promise<Company> - The updated company data
 */
export const updateCompanyContacts = async (
  companyId: string,
  contactData: CompanyContact[]
): Promise<Company> => {
  const { idToken } = JSON.parse(localStorage.getItem('admin-token') || '{}')
  const response = await axios.put(
    `/customers/${companyId}/contacts`,
    contactData,
    {
      baseURL: getApiUrl(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    }
  )

  return response.data
}
